import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, CheckCircle2, Loader2, ScanLine, Trash2, ShieldCheck, ExternalLink, Layers } from "lucide-react";

export const GmailConnect = ({ onScanned }) => {
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [deep, setDeep] = useState(false);

  const load = async () => { try { setStatus(await api.gmailStatus()); } catch {} };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (status?.deep_status === "running") {
      const t = setInterval(load, 3000);
      return () => clearInterval(t);
    }
  }, [status?.deep_status]);

  const connect = async () => {
    if (!email.trim() || !pwd.trim()) { toast.error("Enter your Gmail address and App Password"); return; }
    setConnecting(true);
    try {
      await api.gmailConnect(email.trim(), pwd.trim());
      toast.success("Gmail connected — now scan your inbox");
      setPwd("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not connect Gmail");
    } finally { setConnecting(false); }
  };

  const scan = async () => {
    setScanning(true);
    try {
      const r = await api.gmailScan(25);
      toast.success(r.message || "Inbox scanned");
      await load();
      onScanned?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed");
    } finally { setScanning(false); }
  };

  const disconnect = async () => {
    try { await api.gmailDisconnect(); toast.success("Gmail disconnected"); await load(); }
    catch { toast.error("Could not disconnect"); }
  };

  const deepScan = async () => {
    setDeep(true);
    try {
      const r = await api.gmailDeepScan(2000);
      toast.success(r.message || "Deep scan started");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start deep scan");
    } finally { setDeep(false); }
  };

  const connected = status?.connected;
  const running = status?.deep_status === "running";
  const pct = running && status?.deep_target ? Math.min(100, Math.round((status.deep_done / status.deep_target) * 100)) : 0;

  return (
    <div data-testid="gmail-connect-card" className={`rounded-xl border p-4 mb-4 ${connected ? "border-emerald-500/30 bg-emerald-500/[0.05]" : "border-cyan-500/30 bg-cyan-500/[0.05]"}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg grid place-items-center bg-white shrink-0">
            <Mail size={20} className="text-red-500" />
          </div>
          <div>
            <div className="font-tactical font-bold text-sm text-zinc-100 flex items-center gap-2">
              Connect Gmail — the easy way
              <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300">Recommended</span>
            </div>
            {connected ? (
              <div className="text-[11px] font-mono text-emerald-400 flex items-center gap-1"><CheckCircle2 size={12} /> Connected · {status.email}{status.last_scan ? ` · scanned ${status.scanned || 0} emails` : ""}</div>
            ) : (
              <div className="text-[11px] font-mono text-zinc-500">Paste your email + a 16-char App Password. LifeOS reads your inbox and surfaces real replies to approve.</div>
            )}
          </div>
        </div>
        {connected && (
          <div className="flex items-center gap-2">
            <Button data-testid="gmail-scan-button" onClick={scan} disabled={scanning || running} className="bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-tactical font-bold uppercase text-xs">
              {scanning ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ScanLine size={14} className="mr-1" />}{scanning ? "Scanning" : "Quick scan"}
            </Button>
            <Button data-testid="gmail-deepscan-button" onClick={deepScan} disabled={deep || running} className="bg-white/10 hover:bg-white/20 text-zinc-100 font-tactical font-bold uppercase text-xs">
              {deep || running ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Layers size={14} className="mr-1" />}{running ? "Scanning…" : "Deep scan"}
            </Button>
            <button data-testid="gmail-disconnect-button" onClick={disconnect} className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400"><Trash2 size={14} /></button>
          </div>
        )}
      </div>

      {connected && (running || status?.deep_status === "done") && (
        <div data-testid="gmail-deep-progress" className="mt-3 border-t border-white/10 pt-3">
          {running ? (
            <>
              <div className="flex items-center justify-between text-[11px] font-mono mb-1">
                <span className="text-cyan-300">Deep scan running… reading your inbox</span>
                <span className="text-zinc-400">{status.deep_done?.toLocaleString()} / {status.deep_target?.toLocaleString()}</span>
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-cyan-400 transition-all duration-500" style={{ width: `${pct}%` }} />
              </div>
              <div className="text-[10px] font-mono text-zinc-500 mt-1">{status.deep_people || 0} contacts · {status.deep_credit || 0} credit-related so far · keep this open or come back later</div>
            </>
          ) : (
            <div className="text-[11px] font-mono text-emerald-400 flex items-center gap-1"><CheckCircle2 size={12} /> Deep scan done — read {status.deep_done?.toLocaleString()} emails · {status.deep_people || 0} contacts · {status.deep_credit || 0} credit-related. See them in the Messages tab.</div>
          )}
        </div>
      )}

      {!connected && (
        <div data-testid="gmail-connect-form" className="mt-4 border-t border-white/10 pt-4">
          <ol className="text-[11px] text-zinc-300 space-y-1.5 mb-3 list-decimal pl-4 marker:text-cyan-400">
            <li>Turn on 2-Step Verification (needed for App Passwords): <a href="https://myaccount.google.com/signinoptions/twosv" target="_blank" rel="noreferrer" className="text-cyan-300 underline inline-flex items-center gap-0.5">open <ExternalLink size={10} /></a></li>
            <li>Create a 16-character App Password: <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-cyan-300 underline inline-flex items-center gap-0.5">open App Passwords <ExternalLink size={10} /></a> → name it "LifeOS" → copy the code.</li>
            <li>Paste both below and hit Connect. That's it.</li>
          </ol>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400 font-tactical">Gmail address</Label>
              <Input data-testid="gmail-email-input" type="email" className="bg-[#0b0e14] border-white/10 text-zinc-100 text-sm mt-1" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@gmail.com" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-400 font-tactical">App Password (16 characters)</Label>
              <Input data-testid="gmail-password-input" type="password" className="bg-[#0b0e14] border-white/10 text-zinc-100 text-sm mt-1" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="abcd efgh ijkl mnop" />
            </div>
          </div>
          <Button data-testid="gmail-connect-button" onClick={connect} disabled={connecting} className="mt-3 bg-cyan-500 hover:bg-cyan-400 text-[#04070c] font-tactical font-bold uppercase text-xs">
            {connecting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ShieldCheck size={14} className="mr-1" />}{connecting ? "Connecting" : "Connect Gmail"}
          </Button>
          <p className="text-[10px] text-zinc-500 mt-2">Your App Password is stored encrypted and used only to read your inbox and send replies you approve. Revoke it anytime from your Google account.</p>
        </div>
      )}
    </div>
  );
};
